/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { invoke } from "./invoke.js";
/**
 * Methods for manipulating and interacting with WebView contents in a ToDesktop
 * window.
 *
 * @remarks
 * This package exposes a number of methods for manipulating and interacting
 * with WebView contents in a ToDesktop window.
 *
 * @see {@link https://docs.todesktop.com}
 * @experimental
 * @public
 * @packageDocumentation
 */
import { setupAddEventListener, checkIfCompatibleWithBackend, } from "@todesktop/client-util";
/**
 * Loads a URL into the WebContents
 *
 * @param options - ref: Reference for the WebContents
 * @public
 */
export async function loadURL({ ref }, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "WebContents",
        method: "loadURL",
        args,
    });
}
const _webContentsEventMappings = {
    navigate: "did-navigate",
};
/**
 * Get a list of all available printers on the user's system.
 * @example
 * ```typescript
 * import { printing } from "@todesktop/client";
 *
 * async function logPrinters() {
 *   const printers = await printing.availablePrinters();
 * }
 * ```
 * @public
 */
export async function availablePrinters() {
    return window["todesktop"].contents.getPrinters();
}
/**
 * Adds a print job for the current page to the operating system's queue for
 * immediate printing.
 *
 * @param options - Options to use when processing this print job.
 * @public
 */
export async function print(options) {
    return window["todesktop"].contents.print(options);
}
/**
 * Determine if the history stack has items before the currently loaded URL.
 * @public
 */
export async function canGoBack() {
    return window["todesktop"].contents.canGoBack();
}
/**
 * Determine if the history stack has items ahead of the currently loaded URL.
 * @public
 */
export async function canGoForward() {
    return window["todesktop"].contents.canGoForward();
}
/**
 * @see {@link Electron.WebContents.getZoomLevel}
 * @returns The current zoom level
 * @public
 */
export async function getZoomLevel() {
    if (checkIfCompatibleWithBackend("webContents:getZoomLevel", "1.282.0")) {
        return window["todesktop"].contents.getZoomLevel();
    }
}
/**
 * @see {@link Electron.WebContents.setZoomLevel}
 * @param level - The zoom level to set the current `WebContents` to.
 * @public
 */
export async function setZoomLevel(level) {
    if (checkIfCompatibleWithBackend("webContents:setZoomLevel", "1.282.0")) {
        return window["todesktop"].contents.setZoomLevel(level);
    }
}
/**
 * @see {@link Electron.WebContents.getZoomFactor}
 * @returns The current zoom factor
 * @public
 */
export async function getZoomFactor() {
    if (checkIfCompatibleWithBackend("webContents:getZoomFactor", "1.282.0")) {
        return window["todesktop"].contents.getZoomFactor();
    }
}
/**
 * @see {@link Electron.WebContents.setZoomFactor}
 * @param level - The zoom factor to set the current `WebContents` to.
 * @public
 */
export async function setZoomFactor(factor) {
    if (checkIfCompatibleWithBackend("webContents:setZoomFactor", "1.282.0")) {
        return window["todesktop"].contents.setZoomFactor(factor);
    }
}
/**
 * Toggles whether developer tools are displayed in the current WebContents.
 *
 * @see {@link Electron.WebContents.toggleDevTools}
 * @public
 */
export async function toggleDevTools() {
    if (checkIfCompatibleWithBackend("webContents:toggleDevTools", "1.282.0")) {
        return window["todesktop"].contents.toggleDevTools();
    }
}
/**
 * Explicitly open a specified URL within the desktop app.
 * This bypasses the check and always opens URL in-app and never in the system browser.
 *
 * @param url - URL to open. Example: 'https://www.google.com'
 * @public
 */
export async function openUrlInApp(url) {
    if (checkIfCompatibleWithBackend("webContents:openUrlInApp", "1.292.0")) {
        return window["todesktop"].contents.openUrlInApp(url);
    }
}
/**
 * @public
 */
export const on = setupAddEventListener(_webContentsEventMappings);
